import {
  COMISION_PCT_POR_NIVEL,
  MP_ALIAS_COBRANZAS,
  TRIAL_DIAS,
  UMBRAL_NIVEL_2,
  UMBRAL_NIVEL_3,
  UMBRAL_NIVEL_4,
  UMBRAL_NIVEL_5,
  VENTANA_VENTAS_DIAS,
} from '../../../core/monetizacion/vendedorNiveles.js';
import { escapeHtml as esc } from '../../../core/ui/escapeHtml.js';

function iconBack() {
  return `<svg class="h-6 w-6 text-brand-primary" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><path stroke-linecap="round" stroke-linejoin="round" d="M15.75 19.5L8.25 12l7.5-7.5" /></svg>`;
}

/**
 * Pantalla informativa: trial 30 días, niveles de vendedor y comisiones (alineado a reglas de negocio).
 */
export function createVendedorExperienciaInfoPresenter({ render }) {
  function show({ onBack } = {}) {
    const filasNivel = [
      { nivel: 1, rango: `0–${UMBRAL_NIVEL_2 - 1} ventas`, pct: COMISION_PCT_POR_NIVEL[1] },
      {
        nivel: 2,
        rango: `${UMBRAL_NIVEL_2}–${UMBRAL_NIVEL_3 - 1} ventas`,
        pct: COMISION_PCT_POR_NIVEL[2],
      },
      {
        nivel: 3,
        rango: `${UMBRAL_NIVEL_3}–${UMBRAL_NIVEL_4 - 1} ventas`,
        pct: COMISION_PCT_POR_NIVEL[3],
      },
      {
        nivel: 4,
        rango: `${UMBRAL_NIVEL_4}–${UMBRAL_NIVEL_5 - 1} ventas`,
        pct: COMISION_PCT_POR_NIVEL[4],
      },
      { nivel: 5, rango: `${UMBRAL_NIVEL_5}+ ventas`, pct: COMISION_PCT_POR_NIVEL[5] },
    ];

    render(`
      <div class="min-h-screen bg-brand-surface mlv-bg-surface pb-10">
        <header class="sticky top-0 z-10 flex items-center gap-2 border-b border-slate-200/80 bg-brand-surface/95 px-3 py-3 backdrop-blur-sm">
          <button type="button" id="vexp-back" class="flex h-10 w-10 shrink-0 items-center justify-center rounded-full hover:bg-slate-200/60" aria-label="Volver">
            ${iconBack()}
          </button>
          <h1 class="min-w-0 flex-1 font-heading text-base font-bold tracking-tight text-brand-primary">Experiencia vendedor</h1>
        </header>

        <main class="mx-auto max-w-lg px-4 pt-5 space-y-6 text-sm text-slate-700">
          <section class="rounded-2xl border border-brand-primary/20 bg-brand-primary/5 p-4">
            <h2 class="font-heading text-base font-bold text-brand-primary">Tus primeros ${TRIAL_DIAS} días</h2>
            <p class="mt-2 leading-relaxed">
              Cuando tenés <strong class="text-slate-900">tu primera publicación activa</strong>, arranca un periodo de
              <strong class="text-slate-900">${TRIAL_DIAS} días</strong> en el que la comisión por cada venta marcada como
              vendida se calcula como si fueras <strong class="text-slate-900">nivel 5</strong> (porcentaje más bajo a favor tuyo).
            </p>
            <p class="mt-2 text-xs leading-relaxed text-slate-600">
              Las ventas que cuentan para subir de nivel son las que registrás con <strong class="text-slate-800">«¡Lo vendí!»</strong>
              en Mis publicaciones, dentro de una ventana móvil de <strong class="text-slate-800">${VENTANA_VENTAS_DIAS} días</strong>.
            </p>
          </section>

          <section>
            <h2 class="font-heading text-base font-bold text-slate-900">Niveles según ventas (${VENTANA_VENTAS_DIAS} días)</h2>
            <p class="mt-1 text-xs text-slate-500">
              A más ventas confirmadas en la app en ese período, más sube tu nivel y más baja el % de comisión (excepto durante el trial, donde siempre aplica nivel 5).
            </p>
            <div class="mt-3 overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
              <table class="w-full text-left text-xs">
                <thead class="bg-slate-100 text-[10px] font-bold uppercase tracking-wide text-slate-600">
                  <tr>
                    <th class="px-3 py-2">Nivel</th>
                    <th class="px-3 py-2">Ventas en ventana</th>
                    <th class="px-3 py-2 text-right">Comisión</th>
                  </tr>
                </thead>
                <tbody class="divide-y divide-slate-100">
                  ${filasNivel
                    .map(
                      (f) => `
                    <tr>
                      <td class="px-3 py-2.5 font-semibold text-slate-900">${f.nivel}</td>
                      <td class="px-3 py-2.5 text-slate-600">${esc(f.rango)}</td>
                      <td class="px-3 py-2.5 text-right font-mono font-semibold text-brand-primary">${esc(String(f.pct))}%</td>
                    </tr>`
                    )
                    .join('')}
                </tbody>
              </table>
            </div>
          </section>

          <section class="rounded-2xl border border-amber-200/80 bg-amber-50/60 p-4">
            <h2 class="font-heading text-base font-bold text-amber-950">Después del trial</h2>
            <p class="mt-2 leading-relaxed text-amber-950/90">
              Cuando terminan los ${TRIAL_DIAS} días, la comisión vuelve a calcularse según tu nivel real por ventas en ventana.
              Si completaste el periodo, podés obtener la etiqueta <strong class="text-amber-950">Premio Nivel 5</strong> en tu perfil.
            </p>
          </section>

          <section class="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
            <h2 class="font-heading text-base font-bold text-slate-900">Regularizar comisión</h2>
            <p class="mt-2 leading-relaxed">
              Cada venta confirmada genera una deuda registrada en <strong class="text-slate-900">Mis publicaciones</strong>.
              Podés transferir al alias de Mercado Pago <span class="font-mono font-semibold text-brand-primary">${esc(MP_ALIAS_COBRANZAS)}</span>
              y luego indicar <strong class="text-slate-900">Ya regularicé</strong> (confirmación manual en esta versión).
            </p>
          </section>

          <button type="button" id="vexp-back-bottom" class="w-full rounded-full border-2 border-slate-200 bg-white py-3.5 text-sm font-semibold text-slate-800 hover:bg-slate-50">
            Volver al perfil
          </button>
        </main>
      </div>
    `);

    const back = () => onBack?.();
    document.getElementById('vexp-back').onclick = back;
    document.getElementById('vexp-back-bottom').onclick = back;
  }

  return { show };
}
